import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  inject,
} from '@angular/core';
import { Commands, Downloads, Results, Search } from '@local/client-contracts';
import { isMac } from '@local/common-web';
import { PopupService, TelemetryTarget } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventInfo, EventInfoSearch, EventsService, LogService } from '@shared/services';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService, Shortcuts } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import { componentServicesRpcProvider, ServicesRpcService } from '@shared/services/rpc.service';
import { getShareAssetsPath } from '@shared/utils';
import { isChangeFavoriteStatusCommand, isDownloadUrlCommand, isOpenUrlCommand, isShareCommand } from '@shared/utils/command-util';
import { getDownloadIcon } from '@shared/utils/set-icon.util';
import { Logger } from '@unleash-tech/js-logger';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { IActionItem } from 'src/app/bar/models/action-item.model';
import { HomeResultItemDisplayModel } from 'src/app/bar/models/home-display-model';
import { ShareCommand } from 'src/app/bar/models/share-command';
import { ViewPages, WorkspacesService } from 'src/app/bar/services';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { HubService } from 'src/app/bar/services/hub.service';
import * as uuid from 'uuid';
import { ResultCommandBuilder, TelemetryTrigger, isEnabledSummary, DisplayItemData } from '../../results';
import { SearchPopupItem } from '../../special-views/search-popup/model';
import { SearchPopUpService } from '../../special-views/search-popup/search-popup.service';
import { KeyName } from '@local/ts-infra';
import { Constants } from '@local/common';

@UntilDestroy()
@Component({
  selector: 'home-result-item',
  providers: [componentServicesRpcProvider],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeResultItemComponent<T extends Search.ResultResourceItem = Search.ResultResourceItem & { view: Results.View }>
  implements OnInit, OnDestroy
{
  @Input() set model(value: T) {
    const prev = this.model;
    this._model = value;
    this.isRestricted = this.model?.link?.shareOptions?.restricted;
    if (this.isModelChanged(prev)) {
      this.onChanges();
    }
  }
  @Input() set selected(value: boolean) {
    const prev = this.selected;
    this._selected$.next(value);
    if (this.isSelectionChanged(prev)) {
      this.onChanges();
    }
    if (!value) {
      this.unregisterFromCommandBar();
    }
  }
  @Input() searchInfo: Partial<EventInfoSearch>;
  @Input() index: number;
  @Input() pageNameForTelemetry: ViewPages;
  @Output() action = new EventEmitter();
  @Output() excluded = new EventEmitter();
  isRestricted: boolean;
  confirm$ = new Subject<boolean>();
  private _selected$ = new BehaviorSubject<boolean>(false);
  confirmOptions: Commands.ConfirmMessageOptions = null;
  isIconOverlayEmjoi: boolean;
  shortcuts: Partial<Shortcuts> = {};
  private commandBarId: string;
  private onCommandBarSelect: Subscription;
  private currentHash = null;
  protected _error: any;
  protected _displayModel: HomeResultItemDisplayModel;
  private _model: T;
  private workspacesService: WorkspacesService = inject(WorkspacesService);

  get selected(): boolean {
    return this._selected$.getValue();
  }
  get model() {
    return this._model;
  }
  get displayModel() {
    return this._displayModel;
  }

  set displayModel(value: HomeResultItemDisplayModel) {
    this._displayModel = value;
  }

  protected allActions: Partial<Record<keyof Shortcuts, IActionItem>> = {
    openPreview: {
      id: 'open',
      faIcon: () => 'font-icon  icon-launch',
      toolTipText: 'Open',
      fn: (item, trigger) => item.onOpen(this.model.view.title.onClick, trigger),
    },
    favorite: {
      id: 'favorite',
      faIcon: () => `${this.model.isFavorite ? 'icon-star-solid' : 'icon-star'}  font-icon  `,
      toolTipText: 'Favorite',
      fn: (item, trigger) => item.onFavoriteMark(trigger),
      mode: () => (this.model.isFavorite ? 'remove' : 'add'),
    },
  };

  protected logger: Logger;

  constructor(
    private cdr: ChangeDetectorRef,
    private eventsService: EventsService,
    private services: ServicesRpcService,
    private logService: LogService,
    private commandsService: CommandsService,
    private commandBarService: CommandBarService,
    private popupService: PopupService,
    private keyboardService: KeyboardService,
    private commandBuilder: ResultCommandBuilder,
    private resourcesService: ResourcesService,
    private routerService: RouterService,
    private hubService: HubService,
    @Optional() protected downloadsService: DownloadsService,
    private flagsService: FlagsService,
    private searchPopUpService: SearchPopUpService
  ) {
    this.setLogScope();
  }

  protected setLogScope() {
    this.logger = this.logService.scope('HomeResultItemComponent');
  }

  // Life hooks events
  async ngOnInit(): Promise<void> {
    await this.updateDisplayModel();
  }

  isModelChanged(value): boolean {
    return JSON.stringify(value) !== JSON.stringify(this.model);
  }

  isSelectionChanged(value): boolean {
    return value !== this.selected;
  }

  onChanges() {
    this.setActionsShortcuts();
    if (this.model && this.selected) {
      this.registerToCommandBar();
    }
    this.updateDisplayModel();
    this.isRestricted = this.model?.link?.shareOptions?.restricted;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.unregisterFromCommandBar();
  }

  private setActionsShortcuts() {
    for (const [key, action] of Object.entries(this.allActions)) {
      if (this.shortcuts[key]) action.keyBinding = this.shortcuts[key] as KeyName[];
    }
  }

  // Initializers
  protected async updateDisplayModel() {
    if (!this.model?.view) {
      this._error = true;
      return;
    }

    this.shortcuts = await this.keyboardService.loadShortcuts(['_home']);

    this.displayModel = {
      //@ts-ignore
      icon: this.model.view.icon,
      //@ts-ignore
      iconOverlay: this.model.view.iconOverlay,
      title: this.model.view.title,
      actions: this.getActions(),
      //@ts-ignore
      linkColor: this.model.view.linkColor || null,
      linkName: this.model.view.linkName || null,
    };
    await this.considerSpecialActions();
    this.setActionsShortcuts();
    this.cdr.markForCheck();
  }

  /**
   * @description return the base actions of an item and considers
   * special actions like copy or download
   */
  protected getActions() {
    // Implement non dependent actions

    return [this.allActions.exclude, this.allActions.openPreview, this.allActions.favorite];
  }

  // handlers
  protected onAction(id: string, target: TelemetryTrigger, lbl?: string) {
    const actionItem = this.displayModel.actions.find((action) => action.id === id);
    if (!actionItem) return;
    let label = lbl ? lbl : actionItem.id.toLowerCase().replace(/' '/g, '_');
    if (actionItem.mode) label = `${actionItem.mode()}_${label}`;
    const eventMetadata = this.getEventMetadata(target);
    this.eventsService.event(`${this.pageNameForTelemetry}.interaction_click`, { label, target, ...eventMetadata });
    actionItem.fn(this, target);
  }

  protected async onFavoriteMark(trigger: TelemetryTrigger, source?: string) {
    if (!this.workspacesService.isFeatureDisabled(Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG)) {
      this.model.isFavorite = !this.model.isFavorite;
      const status = this.model.isFavorite ? 'add' : 'remove';
      const command = { type: 'change-favorite-status', status } as Commands.ChangeFavoriteStatus;
      await this.onInvoke(command, trigger, {}, true, source);
      this.cdr.markForCheck();
    }
  }

  // Different functions are defined and doing the same in case one of the child instances will
  // want to override the default behavior , it also makes the code more readable easily;
  protected onDownload(command: Commands.Command, trigger: TelemetryTrigger) {
    const eventMetadata = this.getEventMetadata(trigger);
    this.eventsService.event(`${this.pageNameForTelemetry}.action`, { label: 'download', ...eventMetadata });
    this.onInvoke(command, trigger, null, false);
  }

  protected onCopy(command: Commands.Command, trigger: TelemetryTrigger) {
    this.onInvoke(command, trigger);
  }

  protected onOpen(command: Commands.Command, trigger: TelemetryTrigger) {
    this.onInvoke(command, trigger);
  }

  async onInvoke(
    command: Commands.Command,
    trigger: TelemetryTrigger,
    eventInfo: Partial<EventInfo> = {},
    sendEvent = true,
    source?: string
  ): Promise<void> {
    const eventMetadata = { ...this.getEventMetadata(trigger), ...eventInfo };
    if (source === 'command-bar') {
      eventMetadata.jsonData = { 'action.trigger': 'command_bar' };
    }
    if (sendEvent) {
      switch (command.type) {
        case 'open-url':
          this.eventsService.event(`${this.pageNameForTelemetry}.redirect`, eventMetadata);
          break;
        case 'run-action':
          this.eventsService.event(`${this.pageNameForTelemetry}.action`, {
            label: (command as Commands.RunAction).name,
            ...eventMetadata,
          });
          break;
        case 'copy-clipboard':
          this.eventsService.event(`${this.pageNameForTelemetry}.action`, { label: 'copy_clipboard', ...eventMetadata });
          break;
        case 'change-favorite-status':
          this.eventsService.event(`${this.pageNameForTelemetry}.action`, {
            label: `${(command as Commands.ChangeFavoriteStatus).status}_favorite`,
            ...eventMetadata,
          });
          this.cdr.markForCheck();
          break;
        case 'download-url': {
          this.onDownload(command, trigger);
          break;
        }
        case 'share': {
          this.eventsService.event('results.redirect', { label: 'share', ...eventMetadata });
          break;
        }
      }
    }
    const res = await this.commandsService.executeCommand(command, {
      resource: {
        id: this.model.resource.id,
        kind: 'link-resource',
      },
      linkId: this.model.resource.linkId,
    });
    //TODO: add prompt in the future to allow choosing open-with method
    if (res && !res.data.opened)
      await this.commandsService.executeCommand(command, {
        resource: {
          id: this.model.resource.id,
          kind: 'link-resource',
        },
        linkId: this.model.resource.linkId,
        openWith: 'browser',
      });
  }

  private getEventMetadata(target: string): Partial<EventInfo> {
    return {
      target,
      search: this.searchInfo,
      location: { title: this.hubService.currentLocation },
      resources: [
        {
          id: this.model.resource.id,
          list: this.pageNameForTelemetry,
          appId: this.model.resource.appId,
          type: this.model.resource.type,
          position: this.index,
          linkId: this.model.resource.linkId,
        },
      ],
    };
  }

  private async download(command: Commands.DownloadUrl) {
    if (!isDownloadUrlCommand(command) || !this.downloadsService) return;

    const icon = getDownloadIcon(this.model.view.icon, this.model.view.iconOverlay);
    const req: Downloads.ResourceDownloadRequest = {
      type: 'Resource',
      url: command.url,
      resource: this.model.resource,
      icon,
    };
    await this.downloadsService.resourceDownload(req);
  }
  protected async considerSpecialActions() {
    // Consider Special Actions
    const title: Results.Title = this.model.view.title;
    if (title.onDrag && (await this.isDownloadEnabled())) {
      const hasDownloadAction = this.displayModel.actions.find((a) => a.id === 'download');
      if (hasDownloadAction) return;
      this.displayModel.actions.unshift({
        id: 'download',
        faIcon: () => 'font-icon icon-download2',
        toolTipText: 'Download',
        fn: (item, trigger) => item.download(title.onDrag) && item.onDownload(title.onDrag, trigger || this.shortcuts.download.join('_')),
        keyBinding: this.shortcuts.download as KeyName[],
      });
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: KeyboardEvent) {
    this.onInvoke(this.model.view.title.onClick, 'mouse_click');
  }

  handleKeys(keys: Array<KeyName>): void {
    if (isMac()) {
      keys = keys.map((k) => (k = k === 'control' ? 'command' : k));
    }
    const action = this.displayModel.actions.find((a) => a.keyBinding?.every((k) => keys.includes(k)));
    if (!action) return;
    action.fn(this, 'keyboard');
  }

  async buildPresentableCommands() {
    let {
      link: { appId },
      isFavorite,
    } = this.model;
    if (!this.model.view?.title) {
      return;
    }
    const originTitle = this.model.view.title;

    const { onDrag, onClick, text } = originTitle as Results.Title;
    const downloadUrl: { url: string } | null = await this.getDownloadUrl(onDrag);
    const openUrlCommand: Commands.OpenUrl | null = isOpenUrlCommand(onClick) ? onClick : null;
    const { url, appUri } = openUrlCommand ?? { url: undefined, appUri: undefined };

    return await this.commandBuilder.build({
      resourceId: this.model.resource.id,
      open: { appUri, url },
      isFavorite: { isFavorite },
      copy: { text },
      download: downloadUrl,
      summary: isEnabledSummary((this.model as DisplayItemData)?.action)
        ? { command: this.model.view.title.onClick, resourceId: this.model.id }
        : null,
    });
  }

  private async registerToCommandBar() {
    this.unregisterFromCommandBar();
    if (!this.model) {
      return;
    }

    const children = (await this.buildPresentableCommands())
      .filter((c) => c.id !== 'copy')
      .map<SearchPopupItem<'child'>>((item) => ({
        ...item,
        visibility: 'always',
        title: item.text,
        type: 'child',
        icon: item.icon,
        command: item.command,
      }));
    const id = `result_${this.model.view.title.text.substring(0, 15)}_${uuid.v4()}`;

    this.onCommandBarSelect = this.commandBarService
      .add({
        id,
        type: 'parent',
        title: this.model.view.title.text,
        command: null,
        icon: null,
        visibility: null,
        children,
        data: { sortId: 'COMMANDS' },
      })
      .subscribe(async ({ item: { command, data }, via }) => {
        if (isShareCommand(command)) {
          this.openSharePopup(via, 'command-bar');
          return;
        } else if (isDownloadUrlCommand(command)) {
          this.download({ type: 'download-url', url: data?.url });
        } else if (isChangeFavoriteStatusCommand(command)) {
          await this.onFavoriteMark(via, 'command-bar');
          await this.registerToCommandBar(); // Refresh items
        }
        // We still need our command to onInvoke to report telemetry
        await this.onInvoke(command, via, null, true, 'command-bar');
      });

    this.commandBarId = id;
  }

  private unregisterFromCommandBar() {
    if (this.commandBarId) {
      this.commandBarService.remove(this.commandBarId);
      this.commandBarId = null;
    }
    if (this.onCommandBarSelect) {
      this.onCommandBarSelect.unsubscribe();
    }
  }

  private async openSharePopup(via: TelemetryTarget, source?: string) {
    if (!isOpenUrlCommand(this.model.view.title.onClick)) {
      return;
    }
    this.searchPopUpService.placeholder = 'Share via';

    const shareItems = await this.resourcesService.listShareItems(
      this.model.view.title.onClick.url,
      this.model.resource.id,
      this.model.view.title.text
    );
    const children = shareItems.map<SearchPopupItem<'child'>>(({ icon, id, text, url, desktopUri }) => {
      return {
        id,
        icon: { type: 'img', value: { lightUrl: getShareAssetsPath(icon) } },
        title: text,
        visibility: 'always',
        type: 'child',
        command: { type: 'open-url', url: desktopUri ?? url },
      };
    });
    this.searchPopUpService.add({
      id: `share_${this.index}`,
      icon: null,
      command: null,
      visibility: null,
      title: this.model.view.title.text,
      type: 'parent',
      children,
      sortBy: 'default',
    });
    this.searchPopUpService.select$.pipe(untilDestroyed(this)).subscribe(({ item: { command, title }, via }) => {
      this.onShareItemClicked({ command, text: title }, source);
      this.searchPopUpService.destroy();
    });
    this.searchPopUpService.open(via, 'SharePopup', 'share');
    this.eventsService.event('command_bar.expend_group', {
      location: { title: this.routerService.location },
      label: 'share',
    });
  }

  onShareItemClicked(item: { command: Commands.Command; text: string }, source?: string) {
    const location = { title: this.routerService.active };
    this.commandsService.executeCommand(item.command, {
      resource: {
        id: this.model.resource.id,
        kind: 'link-resource',
      },
      linkId: this.model.resource.linkId,
    });

    this.eventsService.event('results.share_menu', {
      label: item.text,
      location,
      resources: this.getEventResources(),
    });

    const command = { ...item.command, type: 'share' };
    this.onInvoke(command, 'mouse_click', { label: 'share' }, true, source);
  }

  private getEventResources() {
    return [
      {
        id: this.model.id,
        list: 'search_bar_results',
        appId: this.model.link.appId,
        type: this.model.resource.type,
        position: this.index,
        linkId: this.model.link.id,
      },
    ];
  }

  async getDownloadUrl(onDrag: Commands.DragCommand): Promise<{ url: string } | null> {
    return onDrag && isDownloadUrlCommand(onDrag) && this.model.link.appId !== 'pc' && (await this.isDownloadEnabled())
      ? { url: onDrag.url }
      : null;
  }

  protected async isDownloadEnabled(): Promise<boolean> {
    return this.flagsService.isEnabled('fileDownloads', this.model.link.id);
  }
}
